import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { resolveOrgFromToken } from '../auth/utils/resolveOrgFromToken';
import { ORG_HEADER_NAME } from '../auth/utils/constants';
import { LocalStorage } from '../auth/utils/storage';
import { TOKEN_LOCAL_STORAGE_KEY } from '../auth/utils/constants';
import { getAccessToken, getRefreshToken } from '../auth/utils/tokenStorage';
import { getRefreshUrl, refreshAccessToken } from '../auth/utils/refreshToken';
import { setReturnTo } from '../auth/utils/returnTo';

const AUTH_REDIRECT_URL = '/auth';

/** Marks a request that has already been retried, so a failed retry cannot loop. */
type RetriableConfig = InternalAxiosRequestConfig & {
  _retriedAfterRefresh?: boolean;
};

export const axiosInt = axios.create();

axiosInt.defaults.headers.common['Accept-Language'] = 'sr';

axiosInt.interceptors.request.use(config => {
  const token = getAccessToken();

  config.headers = config.headers ?? {};

  // The OAuth2 token endpoint authenticates the client by client_id and PKCE. It rejects the
  // request outright when an Authorization header is present - a valid token fails with
  // invalid_client just as an expired one does - so this endpoint is never decorated.
  if (config.url && config.url === getRefreshUrl()) {
    delete (config.headers as Record<string, string>).Authorization;
    delete (config.headers as Record<string, string>)[ORG_HEADER_NAME];
    return config;
  }

  if (token) {
    (config.headers as Record<string, string>).Authorization =
      `Bearer ${token}`;

    const orgId = resolveOrgFromToken(token);

    if (orgId)
      (config.headers as Record<string, string>)[ORG_HEADER_NAME] = orgId;
    else delete (config.headers as Record<string, string>)[ORG_HEADER_NAME];
  } else {
    delete (config.headers as Record<string, string>).Authorization;
    delete (config.headers as Record<string, string>)[ORG_HEADER_NAME];
  }

  return config;
});

const redirectToLogin = () => {
  LocalStorage.remove(TOKEN_LOCAL_STORAGE_KEY);

  // Remember where the user was so login can bring them back, unless they are already on the auth
  // route - re-entering it would overwrite the original location with the auth page itself.
  if (!window.location.pathname.startsWith(AUTH_REDIRECT_URL)) {
    setReturnTo(`${window.location.pathname}${window.location.search}`);
  }

  window.location.href = AUTH_REDIRECT_URL;
};

/**
 * A 401 usually means the access token expired rather than that the user lost access, so try the
 * refresh token once before giving up and sending them back to the login page.
 */
axiosInt.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const config = error.config as RetriableConfig | undefined;
    const refreshUrl = getRefreshUrl();

    // A 401 from the token endpoint is a rejected grant, not an expired access token. Refreshing
    // would spend a refresh token the server has already invalidated, and redirecting would
    // replace the caller's own error handling - AuthCallback's retry screen - with a navigation.
    if (!!refreshUrl && config?.url === refreshUrl) {
      return Promise.reject(error);
    }

    if (config && !config._retriedAfterRefresh && getRefreshToken()) {
      config._retriedAfterRefresh = true;

      // Only a failed refresh means the session is gone. The replayed request is deliberately
      // outside this try: a 403 or a 500 from it is that request failing, not the session, and
      // treating it as a login problem would sign the user out over a missing permission.
      try {
        await refreshAccessToken();
      } catch {
        redirectToLogin();
        return Promise.reject(error);
      }

      // Replayed through the instance so the request interceptor picks up the new token. If it
      // comes back 401 again it re-enters here, and the _retriedAfterRefresh guard stops the loop.
      return axiosInt(config);
    }

    redirectToLogin();
    return Promise.reject(error);
  }
);

export const setBaseUrl = (url: string): void => {
  axiosInt.defaults.baseURL = url;
};
